import { useState } from "react";
import { AuditTrail, AuditTrailEntry } from "../Components/AuditTrail";
import { OrderStatus, StatusBadge } from "../Components/StatusBadge";
import "./OrderDetail.css";

type MonitorOrderItem = {
  amount: number;
  productNumber?: string | null;
  manuallyAdded?: boolean | null;
  name?: string | null;
  images?: { url: string; name: string; sorting: number }[] | null;
  prodNo?: string | null;
  manualAdd?: boolean | null;
  isMalformed: boolean;
  missingFields: string[];
  status?: number | null;
  replacementProducts?: string[] | null;
};

type MonitorOrder = {
  id: string;
  accountId: string;
  accountName: string;
  orderLines: MonitorOrderItem[];
  automaticOrderDate: string;
  status: string;
  effectiveStatus: string;
  errorMessage?: string | null;
  errorDetails?: Record<string, unknown> | null;
  comment?: string | null;
  createdAt: string;
  updatedAt: string;
  lastOrderedAt?: string | null;
  orderNumber?: string | null;
  orderDate?: string | null;
  deliveryId?: string | null;
  orderChannel?: string | null;
  isBackorder?: boolean;
  isCancelled?: boolean;
  itemCount: number;
  malformedItemCount: number;
  duplicateOrderNumber?: string | null;
};

type OrderDetailProps = {
  order: MonitorOrder;
  auditTrail: AuditTrailEntry[];
  onAutoFix: () => void;
  onResend: () => void;
  onDismiss: () => void;
  onClose: () => void;
  resending?: boolean;
};

const ERROR_TRUNCATE_LENGTH = 2000;

export const OrderDetail = ({
  order,
  auditTrail,
  onAutoFix,
  onResend,
  onDismiss,
  onClose,
  resending = false,
}: OrderDetailProps) => {
  const [showFullError, setShowFullError] = useState(false);

  const errorMessage = order.errorMessage;
  const isErrorTruncated =
    errorMessage != null && errorMessage.length > ERROR_TRUNCATE_LENGTH;
  const displayedError =
    errorMessage != null
      ? showFullError
        ? errorMessage
        : errorMessage.slice(0, ERROR_TRUNCATE_LENGTH)
      : null;

  return (
    <div className="OrderDetail">
      <div className="OrderDetail__header">
        <div>
          <h2 className="OrderDetail__title">
            {order.accountName} — {order.accountId}
          </h2>
          <StatusBadge status={order.effectiveStatus as OrderStatus} isBackorder={order.isBackorder} />
        </div>
        <div className="OrderDetail__actions">
          {order.malformedItemCount > 0 && (
            <button
              className="OrderDetail__action-btn OrderDetail__action-btn--autofix"
              onClick={onAutoFix}
            >
              Auto-ret
            </button>
          )}
          <button
            className="OrderDetail__action-btn OrderDetail__action-btn--resend"
            onClick={onResend}
            disabled={order.status !== "failed" || resending}
          >
            {resending ? "Gensender..." : "Gensend"}
          </button>
          <button
            className="OrderDetail__action-btn OrderDetail__action-btn--dismiss"
            onClick={onDismiss}
            disabled={order.status !== "failed"}
          >
            Afvis
          </button>
          <button
            className="OrderDetail__action-btn OrderDetail__action-btn--close"
            onClick={onClose}
            aria-label="Luk detaljer"
          >
            ✕
          </button>
        </div>
      </div>

      {/* Order Info Section */}
      <div className="OrderDetail__info-grid">
        <div className="OrderDetail__info-item">
          <span className="OrderDetail__info-label">Ordredato</span>
          <span className="OrderDetail__info-value">{formatOrderDate(order.orderDate || order.automaticOrderDate)}</span>
        </div>
        <div className="OrderDetail__info-item">
          <span className="OrderDetail__info-label">Sidst opdateret</span>
          <span className="OrderDetail__info-value">{formatDateTime(order.updatedAt)}</span>
        </div>
        <div className="OrderDetail__info-item">
          <span className="OrderDetail__info-label">Ordrenummer</span>
          <span className="OrderDetail__info-value">{order.orderNumber || "—"}</span>
        </div>
        <div className="OrderDetail__info-item">
          <span className="OrderDetail__info-label">Leverings-ID</span>
          <span className="OrderDetail__info-value">{order.deliveryId || "—"}</span>
        </div>
        <div className="OrderDetail__info-item">
          <span className="OrderDetail__info-label">Kanal</span>
          <span className="OrderDetail__info-value">{order.orderChannel || "—"}</span>
        </div>
        <div className="OrderDetail__info-item">
          <span className="OrderDetail__info-label">Planlagt afsendelse</span>
          <span className="OrderDetail__info-value">{formatOrderDate(order.automaticOrderDate)}</span>
        </div>
      </div>

      {/* Error Message Section */}
      <div className="OrderDetail__section">
        <h3 className="OrderDetail__section-title">Fejlbesked</h3>
        {displayedError != null ? (
          <div className="OrderDetail__error-box">
            <p className="OrderDetail__error-message">
              {displayedError}
              {order.duplicateOrderNumber && (
                <span className="OrderDetail__duplicate-ref">
                  {" "}
                  <a
                    href={`/superadmin/orders?search=${order.duplicateOrderNumber}`}
                    title={`Gå til ordre ${order.duplicateOrderNumber}`}
                    className="OrderDetail__duplicate-link"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ verticalAlign: "middle", marginRight: 4 }}
                    >
                      <circle cx="11" cy="11" r="8" />
                      <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                    {order.duplicateOrderNumber}
                  </a>
                </span>
              )}
            </p>
            {isErrorTruncated && (
              <button
                className="OrderDetail__error-toggle"
                onClick={() => setShowFullError((prev) => !prev)}
              >
                {showFullError ? "Vis mindre" : "Vis mere"}
              </button>
            )}
          </div>
        ) : (
          <p className="OrderDetail__no-error">
            Ingen fejldetaljer tilgængelige
          </p>
        )}
      </div>

      {/* Order Line Items Section */}
      <div className="OrderDetail__section">
        <h3 className="OrderDetail__section-title">
          Ordrelinjer ({order.itemCount} varer
          {order.malformedItemCount > 0 &&
            `, ${order.malformedItemCount} fejlformatterede`}
          )
        </h3>
        <div className="OrderDetail__items-list">
          {order.orderLines.map((item, index) => (
            <OrderLineItem key={index} item={item} index={index} />
          ))}
        </div>
      </div>

      {/* Audit Trail Section */}
      <div className="OrderDetail__audit-section">
        <h3 className="OrderDetail__section-title">Handlingshistorik</h3>
        <AuditTrail entries={auditTrail} />
      </div>
    </div>
  );
};

type OrderLineItemProps = {
  item: MonitorOrderItem;
  index: number;
};

const OrderLineItem = ({ item, index }: OrderLineItemProps) => {
  const discontinuedStatuses = [85, 90, 99];
  const isDiscontinued = item.status != null && discontinuedStatuses.includes(item.status);

  const getStatusLabel = (status: number): string => {
    switch (status) {
      case 85: return "Udgår ved lager 0";
      case 90: return "Udgået";
      case 99: return "Permanent udgået";
      default: return "Udgået";
    }
  };

  return (
    <div
      className={`OrderDetail__item ${
        item.isMalformed ? "OrderDetail__item--malformed" : ""
      } ${isDiscontinued ? "OrderDetail__item--discontinued" : ""}`}
    >
      <div className="OrderDetail__item-header">
        <span className="OrderDetail__item-index">Vare #{index + 1}</span>
        {item.isMalformed && (
          <span className="OrderDetail__item-malformed-badge">
            Fejlformatteret
          </span>
        )}
        {isDiscontinued && (
          <span className="OrderDetail__item-discontinued-badge">
            {getStatusLabel(item.status!)}
          </span>
        )}
      </div>

      <div className="OrderDetail__item-fields">
        <div className="OrderDetail__item-field">
          <span className="OrderDetail__item-field-label">Antal: </span>
          <span className="OrderDetail__item-field-value">{item.amount}</span>
        </div>
        <div className="OrderDetail__item-field">
          <span className="OrderDetail__item-field-label">Produktnummer: </span>
          <span className="OrderDetail__item-field-value">
            {item.productNumber || item.prodNo || "—"}
          </span>
        </div>
        <div className="OrderDetail__item-field">
          <span className="OrderDetail__item-field-label">Manuelt tilføjet: </span>
          <span className="OrderDetail__item-field-value">
            {formatBoolean(item.manuallyAdded ?? item.manualAdd)}
          </span>
        </div>
        <div className="OrderDetail__item-field">
          <span className="OrderDetail__item-field-label">Navn: </span>
          <span className="OrderDetail__item-field-value">
            {item.name || "—"}
          </span>
        </div>
        <div className="OrderDetail__item-field">
          <span className="OrderDetail__item-field-label">Billeder: </span>
          <span className="OrderDetail__item-field-value">
            {item.images ? item.images.length : "—"}
          </span>
        </div>
      </div>

      {isDiscontinued && item.replacementProducts && item.replacementProducts.length > 0 && (
        <div className="OrderDetail__item-replacement">
          <span className="OrderDetail__item-replacement-label">Erstatning: </span>
          <span className="OrderDetail__item-replacement-value">
            {item.replacementProducts.join(", ")}
          </span>
        </div>
      )}

      {/* Side-by-side comparison for malformed items */}
      {item.isMalformed && (
        <>
          <div className="OrderDetail__comparison">
            <h4 className="OrderDetail__comparison-title">
              Legacy vs. påkrævede felter
            </h4>
            <table className="OrderDetail__comparison-table">
              <thead>
                <tr>
                  <th>Felt</th>
                  <th>Legacy værdi</th>
                  <th>Påkrævet felt</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Produktnummer</td>
                  <td>{item.prodNo ?? "—"}</td>
                  <td>productNumber: {item.productNumber ?? "mangler"}</td>
                </tr>
                <tr>
                  <td>Manuelt tilføjet</td>
                  <td>{formatBoolean(item.manualAdd)}</td>
                  <td>manuallyAdded: {formatBoolean(item.manuallyAdded)}</td>
                </tr>
                <tr>
                  <td>Navn</td>
                  <td>—</td>
                  <td>name: {item.name ?? "mangler"}</td>
                </tr>
                <tr>
                  <td>Billeder</td>
                  <td>—</td>
                  <td>images: {item.images ? `[${item.images.length}]` : "mangler"}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {item.missingFields.length > 0 && (
            <div className="OrderDetail__missing-fields">
              <h4 className="OrderDetail__missing-fields-title">
                Manglende felter
              </h4>
              <ul className="OrderDetail__missing-fields-list">
                {item.missingFields.map((field) => (
                  <li key={field} className="OrderDetail__missing-field-tag">
                    {field}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const formatBoolean = (value: boolean | null | undefined): string => {
  if (value === null || value === undefined) return "—";
  return value ? "Ja" : "Nej";
};

const formatOrderDate = (dateStr: string): string => {
  if (!dateStr) return "—";
  // Handle YYYYMMDD format
  if (/^\d{8}$/.test(dateStr)) {
    const year = dateStr.slice(0, 4);
    const month = dateStr.slice(4, 6);
    const day = dateStr.slice(6, 8);
    return `${day}/${month}/${year}`;
  }
  // Handle unix timestamp (ms)
  if (/^\d{10,13}$/.test(dateStr)) {
    const ts = dateStr.length <= 10 ? Number(dateStr) * 1000 : Number(dateStr);
    const date = new Date(ts);
    if (isNaN(date.getTime())) return "—";
    return date.toLocaleDateString("da-DK", { day: "2-digit", month: "2-digit", year: "numeric" });
  }
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("da-DK", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const formatDateTime = (dateStr: string): string => {
  if (!dateStr) return "—";
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("da-DK", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};
